import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { cn } from '@/lib/cn'
import { useAddPurchase } from '@/hooks/usePurchases'
import { BagPlusIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/Spinner'

interface AddPurchaseFormProps {
  /** Chamado só quando a compra entra de fato; erro mantém o campo aberto. */
  onDone: () => void
  autoFocus?: boolean
  className?: string
}

/**
 * Campo para colar o link de um anúncio já comprado.
 *
 * Vive em dois lugares: no popover do cabeçalho (desktop) e dentro do menu
 * sanduíche (celular). A largura vem sempre do contêiner.
 */
export function AddPurchaseForm({ onDone, autoFocus, className }: AddPurchaseFormProps) {
  const [url, setUrl] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const addPurchase = useAddPurchase()

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return

    try {
      await addPurchase.mutateAsync({ productUrl: trimmed })
      setUrl('')
      onDone()
    } catch {
      // O erro já vira toast no hook; o campo fica aberto para nova tentativa.
    }
  }

  return (
    <form onSubmit={handleSubmit} className={cn('flex items-center gap-2', className)}>
      <label htmlFor="purchase-url" className="sr-only">
        Link do produto comprado
      </label>
      <input
        ref={inputRef}
        id="purchase-url"
        type="url"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        placeholder="Insira o link do produto"
        required
        className="h-10 min-w-0 flex-1 rounded bg-surface-muted px-3 text-base text-ink outline-none placeholder:text-ink-faint focus:ring-2 focus:ring-brand/40 lg:text-sm"
      />
      <button
        type="submit"
        disabled={addPurchase.isPending}
        aria-label="Adicionar compra"
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded bg-brand text-white transition-colors hover:bg-brand-dark',
          addPurchase.isPending && 'opacity-60',
        )}
      >
        {addPurchase.isPending ? (
          <Spinner size={18} label={null} />
        ) : (
          <BagPlusIcon className="size-5" />
        )}
      </button>
    </form>
  )
}
